import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCmsPage, c } from '../lib/cms';

const DEFAULT_MODULES = [
    { name: 'My Dashboard', desc: 'At-a-glance financial overview: today\'s production, MTD/YTD metrics, collections, outstanding AR, write-off analysis, and 6-month trends. Filterable by date range, payer, provider, and location.', category: 'FINANCIAL' },
    { name: 'Schedule', desc: 'Intelligent appointment management with drag-and-drop scheduling, multi-provider views, operatory management, and automated patient reminders. Handles complex multi-location scheduling seamlessly.', category: 'OPERATIONS' },
    { name: 'Appointments', desc: 'Real-time appointment tracking with check-in/check-out workflows, wait time monitoring, and production tracking per visit. Integrates with patient communications for confirmations and follow-ups.', category: 'OPERATIONS' },
    { name: 'Patients', desc: 'Comprehensive patient records with demographics, insurance verification, treatment history, document management, and family linking. Quick search finds any patient in milliseconds.', category: 'CLINICAL' },
    { name: 'Claims', desc: 'Electronic claims submission with real-time status tracking, batch processing, ERA/EOB auto-posting, and clearinghouse integration. AI surfaces claims that need attention before they become problems.', category: 'FINANCIAL' },
    { name: 'Pre-Auth', desc: 'Automated pre-authorization request management. Track approval status, attach clinical documentation, and manage the full authorization lifecycle without leaving the system.', category: 'FINANCIAL' },
    { name: 'Appeals', desc: 'Denial appeal workflows with template-based letter generation, CARC/RARC tracking, deadline management, and outcome analytics. Connected to Denial IQ for root cause intelligence.', category: 'FINANCIAL' },
    { name: 'Billing', desc: 'Patient billing, payment plans, credit card processing, statement generation, and collections workflows. Real-time AR aging and automated follow-up reminders.', category: 'FINANCIAL' },
    { name: 'Reports', desc: 'Built-in reporting plus the full InsightFlow analytics suite. Financial, production, operational, and clinical reports with drill-down to provider, location, and payer level.', category: 'ANALYTICS' },
    { name: 'Treat Plans', desc: 'Visual treatment planning with tooth-level charting, multi-phase plans, insurance estimation, and patient presentation mode. AI suggests optimal treatment sequencing.', category: 'CLINICAL' },
    { name: 'Recall', desc: 'Automated patient recall management with configurable intervals, multi-channel reminders (text, email, phone), and effectiveness tracking per channel and message.', category: 'CLINICAL' },
    { name: 'Medical History', desc: 'Comprehensive medical history forms with medication tracking, allergy alerts, contraindication checking, and integration with clinical decision support.', category: 'CLINICAL' },
    { name: 'Imaging', desc: 'Integration with all major dental imaging systems. View, annotate, and compare radiographs and photos directly within the patient chart. AI-assisted image analysis.', category: 'CLINICAL' },
    { name: 'Commlog', desc: 'Complete communication history per patient — calls, texts, emails, portal messages, and in-office notes. Everything in one timeline, searchable and auditable.', category: 'COMMUNICATIONS' },
    { name: 'Referrals', desc: 'Referral tracking from creation through completion. Manage referring and receiving provider relationships, track referral outcomes, and generate thank-you correspondence.', category: 'COMMUNICATIONS' },
    { name: 'Lab Cases', desc: 'Lab case management with vendor integration, case tracking, quality control, and cost analytics. Know where every case is and what it costs.', category: 'OPERATIONS' },
];

const categories = ['FINANCIAL', 'CLINICAL', 'OPERATIONS', 'ANALYTICS', 'COMMUNICATIONS'];

export default function FeaturesPage() {
  const cmsPage = useCmsPage('xentrios', 'features');
  const heroHeadline = c(cmsPage, 'hero', 'headline', 'Every module your practice needs.\nOne system.');
  const heroSubtitle = c(cmsPage, 'hero', 'subtitle', 'Xentrios covers the complete practice workflow — scheduling, clinical, billing, analytics, and communications — with AI assistance throughout.');
  const modules = c(cmsPage, 'modules', 'items', DEFAULT_MODULES);

  return (
    <div>
      <Helmet><title>Features — Xentrios Dental PMS</title></Helmet>

      <section className="xen-hero" style={{ padding: '4rem 0 3rem' }}>
        <div className="xen-container">
          <p className="xen-hero-eyebrow">Features</p>
          <h1>{heroHeadline}</h1>
          <p className="subtitle">{heroSubtitle}</p>
        </div>
      </section>

      {/* AI Section */}
      <section className="xen-section xen-section-center">
        <div className="xen-container">
          <p className="xen-section-eyebrow">AI-Enabled</p>
          <h2>AI that works for your team, not instead of them</h2>
          <p className="xen-section-subtitle" style={{ marginBottom: '2.5rem' }}>
            Xentrios uses AI to reduce the administrative burden on clinical staff. Our approach is transparent —
            AI informs and orients instead of making decisions that are hidden from everyone.
          </p>
          <div className="xen-features">
            <div className="xen-feature" style={{ borderTop: '3px solid var(--xen-accent)' }}>
              <h3>Smart Claim Scrubbing</h3>
              <p>AI reviews claims before submission, flags common rejection patterns, and suggests corrections. Your team sees every suggestion and decides what to change.</p>
            </div>
            <div className="xen-feature" style={{ borderTop: '3px solid var(--xen-teal)' }}>
              <h3>Treatment Sequencing</h3>
              <p>AI suggests optimal treatment plan ordering based on clinical priority, insurance coverage, and patient history. Providers review and approve — nothing happens automatically.</p>
            </div>
            <div className="xen-feature" style={{ borderTop: '3px solid var(--xen-accent)' }}>
              <h3>Scheduling Optimization</h3>
              <p>AI identifies scheduling gaps, suggests appointment types that fit available time, and predicts no-show risk. Front desk staff makes the final call.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Module breakdown by category */}
      {categories.map(cat => (
        <section key={cat} className={`xen-section ${cat === 'CLINICAL' || cat === 'COMMUNICATIONS' ? 'xen-section-light' : ''}`}>
          <div className="xen-container">
            <p className="xen-section-eyebrow">{cat}</p>
            <div className="xen-modules">
              {modules.filter(m => m.category === cat).map(m => (
                <div key={m.name} className="xen-module">
                  <div>
                    <h4>{m.name}</h4>
                    <p>{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ))}

      {/* CTA */}
      <section className="xen-section xen-section-dark" style={{ textAlign: 'center', padding: '4rem 0' }}>
        <div className="xen-container">
          <h2 style={{ color: '#fff' }}>See it all in action</h2>
          <p style={{ color: 'var(--xen-gray)', maxWidth: 450, margin: '0 auto 2rem' }}>
            30-minute personalized walkthrough of every module relevant to your practice.
          </p>
          <Link to="/demo" className="xen-btn xen-btn-primary" style={{ padding: '0.85rem 2.5rem', fontSize: '1rem' }}>Request a Demo</Link>
        </div>
      </section>
    </div>
  );
}
